// React imports
import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
// Import typed Redux hooks
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import { useIsTablet } from "./hooks/useIsTablet";

// Import child components
import ChatbotLayout from "./ChatbotLayout_Component";
import DealsTab from "./DealsTab_Component";
import DocsTab from "./DocsTab_Component";
import DocumentPreviewTab from "./DocumentPreviewTab_Component";
import ResizableDivider from "./ResizableDivider_Component";
import DealDetailsPanel from "./DealDetailsPanel_Component";
import DealDocsPanel from "./DealDocsPanel_Component";

// Import Redux selectors and actions
import {
  selectIsPreviewTabVisible,
  selectOpenDocuments,
  selectActiveDocumentId,
  selectOpenSequence,
  closeDocumentPreview,
  closeAllDocumentPreviews,
  setActiveDocument
} from "./DocumentPreviewSlice_Store";
import { setLayoutMode, selectLayoutMode } from "./LayoutSlice_Store";
import { selectHasWorkspaceResults } from "./ResultsSlice_Store";
import { selectCurrentProjectType } from "./ProjectSlice_Store";
import { selectSelectedUseCase } from "./ChatSlice_Store";
import {
  selectSelectedDealId,
  selectSelectedDealTab,
  selectDealDetail,
  closeDealDetail,
  setSelectedDealTab,
} from "./DealsTableSlice_Store";
import { useFeatureFlag } from "./hooks/useFeatureFlag";

// Default chat width percentage (in-memory only)
const DEFAULT_CHAT_WIDTH = 33.33;
const TABLET_CHAT_WIDTH = 40;

// Default export of DealsLayout_Component
export default function DealsLayout_Component(): JSX.Element {
  const dispatch = useAppDispatch();
  const isTablet = useIsTablet();

  // State to manage the active tab. 'Deals' is the default.
  const [activeTab, setActiveTab] = useState<string>("Deals");
  // Mobile view state - true for chat, false for results
  const [showChat, setShowChat] = useState<boolean>(true);

  // Get workspace results state
  const hasWorkspaceResults = useAppSelector(selectHasWorkspaceResults);

  // Resizable panels state - default to 33.33% for chat, 66.67% for workspace
  // Width is maintained in memory only and not persisted between sessions
  const [chatWidth, setChatWidth] = useState<number>(DEFAULT_CHAT_WIDTH);

  // Track dragging state to disable transitions during drag
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Control workspace entrance animation phases
  const [animationPhase, setAnimationPhase] = useState<'none' | 'scrolling' | 'resizing' | 'complete'>('none');
  const [workspaceOpacity, setWorkspaceOpacity] = useState<number>(0);

  // Refs for smooth drag handling without triggering re-renders
  const chatWidthRef = useRef<number>(DEFAULT_CHAT_WIDTH);
  const dividerRafRef = useRef<number | null>(null);
  const splitLayoutRef = useRef<HTMLDivElement | null>(null);
  const isTabletRef = useRef<boolean>(isTablet);

  // Adjust chat width for tablet (2/5ths) vs desktop (1/3rd)
  useEffect(() => {
    isTabletRef.current = isTablet;
    const targetWidth = isTablet ? TABLET_CHAT_WIDTH : DEFAULT_CHAT_WIDTH;
    setChatWidth(targetWidth);
    chatWidthRef.current = targetWidth;
  }, [isTablet]);

  // Handle divider drag move - updates DOM directly via RAF without React state
  const handleDividerDragMove = useCallback((clientX: number): void => {
    // Cancel any pending RAF
    if (dividerRafRef.current !== null) {
      cancelAnimationFrame(dividerRafRef.current);
    }

    dividerRafRef.current = requestAnimationFrame(() => {
      if (!splitLayoutRef.current) {
        dividerRafRef.current = null;
        return;
      }

      // Get the actual split container bounds
      const rect = splitLayoutRef.current.getBoundingClientRect();

      // Calculate position relative to the container (not viewport)
      const relativeX = clientX - rect.left;
      const newWidthPercent = (relativeX / rect.width) * 100;

      // Tighter constraints on tablet to keep both panels usable
      const min = isTabletRef.current ? 30 : 20;
      const max = isTabletRef.current ? 70 : 80;
      const constrainedWidth = Math.max(min, Math.min(max, newWidthPercent));

      // Update ref value
      chatWidthRef.current = constrainedWidth;

      // Update CSS custom property directly on the split layout container
      splitLayoutRef.current.style.setProperty('--chat-panel-width', `${constrainedWidth}%`);

      dividerRafRef.current = null;
    });
  }, []);

  // Handle divider drag end - commits ref value to React state and re-enables transitions
  const handleDividerDragEnd = useCallback((): void => {
    // Clear any pending RAF
    if (dividerRafRef.current !== null) {
      cancelAnimationFrame(dividerRafRef.current);
      dividerRafRef.current = null;
    }

    // Commit the ref value to state once
    setChatWidth(chatWidthRef.current);

    // Re-enable transitions
    setIsDragging(false);
  }, []);

  // Handle divider drag start - disables transitions for smooth dragging
  const handleDividerDragStart = useCallback((): void => {
    setIsDragging(true);
  }, []);

  // Document preview state
  const isPreviewTabVisible = useAppSelector(selectIsPreviewTabVisible);
  const openDocuments = useAppSelector(selectOpenDocuments);
  const activeDocumentId = useAppSelector(selectActiveDocumentId);

  // Get layout mode from Redux
  const layoutMode = useAppSelector(selectLayoutMode);

  // Get use case from both sources:
  // - selectedUseCase: Set when user clicks conversation starter (new project)
  // - projectType: Loaded from backend PROJECTS table (reloaded project)
  const selectedUseCase = useAppSelector(selectSelectedUseCase);
  const projectType = useAppSelector(selectCurrentProjectType);
  const effectiveUseCase = selectedUseCase || projectType;

  // Inline deals feature flag + deal detail state
  const inlineDealsFlag = useFeatureFlag("inlineDeals");
  const selectedDealId = useAppSelector(selectSelectedDealId);
  const selectedDealTab = useAppSelector(selectSelectedDealTab);
  const selectedDealData = useAppSelector((state) => selectDealDetail(state, selectedDealId));
  const hasDealSelected = inlineDealsFlag && !!selectedDealId;

  // Count docs for selected deal from documentRegistry (deal API doesn't return documents)
  const dealDocsCount = useAppSelector((state) => {
    if (!selectedDealId) return 0;
    const registry = state.resultsSlice?.documentRegistry || [];
    return registry.filter((d: any) => d.dealId === selectedDealId).length;
  });

  // Check if we have any results to show the workspace.
  // When inline flag ON, top-level deals/documents shouldn't force the workspace open —
  // the workspace opens only from explicit actions (deal click, doc preview, facts dispatch).
  const deals = useAppSelector((state) => state.resultsSlice?.deals || []);
  const documents = useAppSelector((state) => state.resultsSlice?.documents || []);
  const hasResults = inlineDealsFlag
    ? openDocuments.length > 0 || hasDealSelected
    : deals.length > 0 || documents.length > 0 || openDocuments.length > 0;

  // Show workspace if layoutMode is 'workspace' OR if we have results
  // layoutMode === 'workspace' keeps the panel visible even when results are empty
  // (e.g. search returned 0 deals — show the empty-state table, don't collapse)
  const showWorkspace = layoutMode === 'workspace' || hasResults;

  // Orchestrate three-phase workspace entrance animation
  useLayoutEffect(() => {
    if (hasResults && animationPhase === 'none') {
      // Phase 1: Set initial state - chat at 100%, workspace hidden, transitions OFF
      setAnimationPhase('scrolling');
      setWorkspaceOpacity(0);

      if (splitLayoutRef.current) {
        // Force the chat to 100% width immediately without transition
        const chatPanel = splitLayoutRef.current.querySelector('.flex-none') as HTMLElement | null;
        if (chatPanel) {
          chatPanel.style.transition = 'none';
          chatPanel.style.width = '100%';

          // Force a reflow to ensure the style is applied
          void chatPanel.offsetHeight;

          // Re-enable transitions for the resize phase
          requestAnimationFrame(() => {
            chatPanel.style.transition = '';

            // Phase 2: Trigger resize to target width
            setTimeout(() => {
              setAnimationPhase('resizing');

              // Phase 3: Fade in workspace after resize completes
              setTimeout(() => {
                setAnimationPhase('complete');
                setWorkspaceOpacity(1);
              }, 500); // Match CSS transition duration
            }, 50); // Small delay to ensure transition is re-enabled
          });
        }
      }
    } else if (!hasResults && animationPhase !== 'none') {
      // Only reset animation when layoutMode is no longer 'workspace'.
      // When a search returns 0 results, layoutMode stays 'workspace' and
      // we keep the panel visible to show the empty-state table.
      if (layoutMode !== 'workspace') {
        setAnimationPhase('none');
        setWorkspaceOpacity(0);
        setShowChat(true); // On mobile, flip back to Chat view for new conversation
      }
    }
  }, [hasResults, animationPhase, layoutMode]);

  // Define base tabs - hide for offer-letter, hide "Deals" tab when inline flag ON.
  // For research-desk: hide the workspace "Docs" tab once a preview is open
  // (keeps the UX clean when a row click produced a preview tab), but keep it
  // present when no preview is open so the workspace doesn't render blank
  // when setLayoutMode('workspace') fires on facts-arrival with no click.
  const baseTabs = React.useMemo(() => {
    if (effectiveUseCase === "offer-letter") return [];
    if (effectiveUseCase === "research-desk") {
      return openDocuments.length > 0 ? [] : ["Docs"];
    }
    return inlineDealsFlag ? ["Docs"] : ["Deals", "Docs"];
  }, [effectiveUseCase, inlineDealsFlag, openDocuments.length]);

  // Returns the count badge value for a tab (deals or docs)
  const getTabCount = (tab: string): number => {
    if (tab === "Deals") return deals.length;
    if (tab === "Docs") return documents.length;
    return 0;
  };
  // Human-readable tab label: research docs expose researchTitle (their
  // originalFilename is an opaque source ID), deal docs use originalFilename.
  // Must be used everywhere a tab is named or looked up so the activeTab
  // string stays consistent across open/close/switch logic.
  const getDocTabName = (doc: { researchTitle?: string; originalFilename?: string }) =>
    doc.researchTitle || doc.originalFilename || "Document Preview";

  const documentTabs = openDocuments.map(doc => ({
    id: doc.docId,
    name: getDocTabName(doc as { researchTitle?: string; originalFilename?: string }),
    document: doc
  }));
  const tabs = [...baseTabs, ...documentTabs];

  // All valid tab names — deal tabs replace base tabs when a deal is selected,
  // so the auto-fallback effect treats them as first-class members.
  const allTabNames = React.useMemo(() => {
    const primary = hasDealSelected ? ["DealSummary", "DealDocs"] : baseTabs;
    return [...primary, ...documentTabs.map(dt => dt.name)];
  }, [hasDealSelected, baseTabs, documentTabs]);
  React.useEffect(() => {
    if (showWorkspace && allTabNames.length > 0 && !allTabNames.includes(activeTab)) {
      setActiveTab(allTabNames[0]);
    }
  }, [showWorkspace, allTabNames, activeTab]);

  // Auto-switch to deal panel tabs when a deal is selected.
  // When the deal closes, allTabNames changes (deal tabs removed, base tabs restored)
  // and the existing auto-fallback effect above naturally picks the first valid tab.
  const prevSelectedDealId = React.useRef(selectedDealId);
  React.useEffect(() => {
    if (hasDealSelected && selectedDealId !== prevSelectedDealId.current) {
      setActiveTab(selectedDealTab === "docs" ? "DealDocs" : "DealSummary");
      setShowChat(false); // On mobile, flip to RHS view
    }
    prevSelectedDealId.current = selectedDealId;
  }, [selectedDealId, hasDealSelected, selectedDealTab]);

  // Auto-switch to Document Preview tab on every openDocumentPreview dispatch
  const openSequence = useAppSelector(selectOpenSequence);
  const prevOpenSequence = React.useRef(openSequence);

  React.useEffect(() => {
    if (openSequence !== prevOpenSequence.current) {
      // A document was explicitly opened (fact badge, source link, etc.)
      if (activeDocumentId) {
        const activeDoc = openDocuments.find(doc => doc.docId === activeDocumentId);
        if (activeDoc) {
          setActiveTab(getDocTabName(activeDoc));
        }
      }
      setShowChat(false); // On mobile, flip to the Deals & Docs view
    }
    prevOpenSequence.current = openSequence;
  }, [openSequence, activeDocumentId, openDocuments]);

  const toggleView = (): void => {
    setShowChat(!showChat);
  };

  const handleCloseDocumentPreview = (docId: string): void => {
    dispatch(closeDocumentPreview(docId));

    // If closing the currently active tab, switch to another tab
    const closedDoc = openDocuments.find(doc => doc.docId === docId);
    if (closedDoc && activeTab === getDocTabName(closedDoc)) {
      // Switch to the last remaining document tab, or to the first base tab
      const remainingDocs = openDocuments.filter(doc => doc.docId !== docId);
      if (remainingDocs.length > 0) {
        setActiveTab(getDocTabName(remainingDocs[remainingDocs.length - 1]));
      } else if (baseTabs.length > 0) {
        setActiveTab(baseTabs[0]);
      }
    }

    // Check if this was the last document and there are no other reasons to keep RHS open
    const remainingDocs = openDocuments.filter(doc => doc.docId !== docId);
    if (remainingDocs.length === 0 && !hasDealSelected && (inlineDealsFlag || !hasWorkspaceResults)) {
      dispatch(setLayoutMode('chat'));
    }
  };

  /** Close the deal detail panel and collapse RHS if no doc previews are open */
  const handleCloseDealDetail = (): void => {
    dispatch(closeDealDetail());
    if (openDocuments.length === 0) {
      dispatch(setLayoutMode("chat"));
    }
  };

  /** Global panel close — wipes open previews + deal detail and re-centers chat. */
  const handleClosePanel = (): void => {
    dispatch(closeAllDocumentPreviews());
    dispatch(closeDealDetail());
    dispatch(setLayoutMode("chat"));
  };

  /**
   * Renders the content for the currently active tab.
   * @returns {JSX.Element | null} The component for the active tab.
   */
  const renderContent = (): JSX.Element | null => {
    switch (activeTab) {
      case "DealSummary":
        return <DealDetailsPanel />;
      case "DealDocs":
        return <DealDocsPanel />;
      case "Deals":
        return <DealsTab />;
      case "Docs":
        return <DocsTab />;
      default:
        // Check if this is a document preview tab (dynamic name)
        const documentTab = documentTabs.find(tab => tab.name === activeTab);
        if (documentTab) {
          return (
            <DocumentPreviewTab
              document={documentTab.document}
              onClose={() => handleCloseDocumentPreview(documentTab.id)}
            />
          );
        }
        return null;
    }
  };

  return (
    <div className="w-full h-full bg-transparent">
      {/* Mobile Layout - iOS Style Toggle */}
      <div className="md:hidden h-full flex flex-col">
        {/* iOS Style Toggle Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex justify-center">
            {/* iOS Style Toggle Switch */}
            <div className="relative inline-flex items-center bg-gray-100 rounded-full p-1 w-72">
              {/* Background slider */}
              <div 
                className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-full shadow-md transition-transform duration-300 ease-in-out ${
                  showChat ? 'translate-x-1' : 'translate-x-[calc(100%+6px)]'
                }`}
              />
              
              {/* Chat Option */}
              <button
                onClick={() => setShowChat(true)}
                className={`relative z-10 flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium rounded-full transition-colors duration-200 ${
                  showChat
                    ? 'text-cvc-blue'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <div className="w-5 h-5 bg-cvc-blue rounded-full flex items-center justify-center mr-2">
                  <span className="text-white text-xs font-medium">AI</span>
                </div>
                Chat
              </button>

              {/* Results Option */}
              <button
                onClick={() => setShowChat(false)}
                className={`relative z-10 flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium rounded-full transition-colors duration-200 ${
                  !showChat
                    ? 'text-cvc-blue'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <svg className="w-4 h-4 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
                </svg>
                Table
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Content Area - Full Screen */}
        <div className="flex-1 min-h-0">
          {showChat ? (
            /* Full Screen Chat View */
            <ChatbotLayout chatInputAriaLabel="Mobile message input field" />
          ) : (
            /* Full Screen Results View */
            <div className="h-full flex flex-col">
              {/* Tab Navigation - Horizontal scroll on mobile */}
              <div className="flex-shrink-0 border-b border-cvc-grey overflow-x-auto bg-white">
                <div className="flex min-w-max">
                  {hasDealSelected ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 py-3 px-4 text-sm font-cvcBody font-semibold text-cvc-text truncate max-w-[150px]">
                        {selectedDealData?.dealName || "Deal"}
                      </span>
                      <button
                        onClick={() => { dispatch(setSelectedDealTab("summary")); setActiveTab("DealSummary"); }}
                        className={`relative inline-flex items-center py-3 px-4 text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out whitespace-nowrap ${
                          activeTab === "DealSummary"
                            ? "border-b-2 border-cvc-blue text-cvc-blue"
                            : "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent"
                        }`}
                      >
                        Summary
                      </button>
                      <button
                        onClick={() => { dispatch(setSelectedDealTab("docs")); setActiveTab("DealDocs"); }}
                        className={`relative inline-flex items-center gap-1.5 py-3 px-4 text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out whitespace-nowrap ${
                          activeTab === "DealDocs"
                            ? "border-b-2 border-cvc-blue text-cvc-blue"
                            : "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent"
                        }`}
                      >
                        Docs
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseDealDetail();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              handleCloseDealDetail();
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          className="ml-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                          aria-label="Close deal details"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </span>
                      </button>
                    </>
                  ) : (
                    baseTabs.map((tab) => {
                      const count = getTabCount(tab);
                      return (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`relative inline-flex items-center gap-1.5 py-3 px-4 text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out whitespace-nowrap ${
                            activeTab === tab
                              ? "border-b-2 border-cvc-blue text-cvc-blue"
                              : "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent"
                          }`}
                        >
                          {count > 0 && (
                            <span className="inline-flex items-center justify-center rounded-full bg-cvc-blue/10 text-cvc-blue text-xs font-normal px-1.5 h-[20px] min-w-[20px]">
                              {count}
                            </span>
                          )}
                          <span className="truncate max-w-[150px]">{tab}</span>
                        </button>
                      );
                    })
                  )}
                  {documentTabs.map((docTab) => (
                    <button
                      key={docTab.id}
                      onClick={() => setActiveTab(docTab.name)}
                      className={`relative inline-flex items-center py-3 px-4 text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out whitespace-nowrap ${
                        activeTab === docTab.name
                          ? "border-b-2 border-cvc-blue text-cvc-blue"
                          : "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent"
                      }`}
                      title={docTab.name}
                    >
                      <span className="truncate max-w-[150px]">{docTab.name}</span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseDocumentPreview(docTab.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCloseDocumentPreview(docTab.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                        aria-label="Close document preview"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Results Content Area */}
              <div className="flex-1 overflow-y-auto">{renderContent()}</div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Layout - Conditional: Centered vs Split */}
      <div className="hidden md:flex h-full">
        {!showWorkspace ? (
          /* Centered Chat Mode - No Results Yet */
          <div className="flex-1 flex justify-center h-full">
            <div className="w-full max-w-5xl h-full flex flex-col">
              {/* White container - content area */}
              <div className="flex-1 min-h-0 bg-white shadow-sm border border-cvc-grey overflow-hidden flex flex-col mx-4 mt-4">
                <ChatbotLayout chatInputAriaLabel="Desktop message input field" />
              </div>
            </div>
          </div>
        ) : (
          /* Split Layout Mode - Results Available */
          <div className="flex-1 h-full tablet:p-4 overflow-hidden">
          <div
            ref={splitLayoutRef}
            className="flex h-full overflow-hidden"
            style={{
              ['--chat-panel-width' as any]: `${chatWidth}%`
            } as React.CSSProperties}
          >
            {/* Left column for ChatbotLayout */}
            <div
              className={`flex-none overflow-hidden h-full ${isDragging ? '' : 'transition-all duration-500 ease-in-out'}`}
              style={{
                width: animationPhase === 'scrolling' ? '100%' : 'var(--chat-panel-width, 33.33%)'
              }}
            >
              <ChatbotLayout chatInputAriaLabel="Desktop message input field" inWorkspaceMode={true} />
            </div>

            {/* Resizable Divider */}
            <ResizableDivider
              onDragStart={handleDividerDragStart}
              onDragMove={handleDividerDragMove}
              onDragEnd={handleDividerDragEnd}
            />

            {/* Right column for Tabbed Content */}
            <div
              className={`flex-1 min-w-0 overflow-hidden h-full flex flex-col transition-opacity duration-500 ease-in-out ${isDragging ? '' : 'transition-all duration-500 ease-in-out'}`}
              style={{ opacity: workspaceOpacity }}
            >
              {/* Tab Navigation Bar */}
              <div className="flex items-center flex-shrink-0 border-b border-cvc-grey tablet:sticky tablet:top-0 tablet:z-10 tablet:bg-white">
                {/* Tabs scroll horizontally if they overflow; close button stays anchored. */}
                <div className="flex items-center min-w-0 flex-1 overflow-x-auto">
                {hasDealSelected ? (
                  <>
                    {/* Deal-specific header: deal name + Summary/Docs tabs + close */}
                    <span className="inline-flex items-center gap-1.5 py-2 px-5 text-sm font-cvcBody font-semibold text-cvc-text truncate max-w-[200px]">
                      {selectedDealData?.dealName || "Deal"}
                    </span>
                    <button
                      onClick={() => { dispatch(setSelectedDealTab("summary")); setActiveTab("DealSummary"); }}
                      className={`relative inline-flex items-center py-2 px-5 text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out ${
                        activeTab === "DealSummary"
                          ? "border-b-2 border-cvc-blue text-cvc-blue"
                          : "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent"
                      }`}
                    >
                      Summary
                    </button>
                    <button
                      onClick={() => { dispatch(setSelectedDealTab("docs")); setActiveTab("DealDocs"); }}
                      className={`relative inline-flex items-center gap-1.5 py-2 px-5 text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out ${
                        activeTab === "DealDocs"
                          ? "border-b-2 border-cvc-blue text-cvc-blue"
                          : "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent"
                      }`}
                    >
                      {dealDocsCount > 0 && (
                        <span className="inline-flex items-center justify-center rounded-full bg-cvc-blue/10 text-cvc-blue text-xs font-normal px-1.5 h-[20px] min-w-[20px]">
                          {dealDocsCount}
                        </span>
                      )}
                      Docs
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseDealDetail();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCloseDealDetail();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                        aria-label="Close deal details"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </span>
                    </button>
                  </>
                ) : (
                  baseTabs.map((tab) => {
                    const count = getTabCount(tab);
                    return (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`relative inline-flex items-center gap-1.5 py-2 px-5 text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out ${
                          activeTab === tab
                            ? "border-b-2 border-cvc-blue text-cvc-blue"
                            : "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent"
                        }`}
                      >
                        {count > 0 && (
                          <span className="inline-flex items-center justify-center rounded-full bg-cvc-blue/10 text-cvc-blue text-xs font-normal px-1.5 h-[20px] min-w-[20px]">
                            {count}
                          </span>
                        )}
                        <span className="truncate max-w-[200px]">{tab}</span>
                      </button>
                    );
                  })
                )}
                {documentTabs.map((docTab) => (
                  <button
                    key={docTab.id}
                    onClick={() => setActiveTab(docTab.name)}
                    className={`relative inline-flex items-center py-2 px-5 text-sm font-cvcBody font-normal focus:outline-none transition-colors duration-200 ease-in-out ${
                      activeTab === docTab.name
                        ? "border-b-2 border-cvc-blue text-cvc-blue"
                        : "text-cvc-text hover:text-cvc-blue border-b-2 border-transparent"
                    }`}
                    title={docTab.name}
                  >
                    <span className="truncate max-w-[200px]">{docTab.name}</span>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseDocumentPreview(docTab.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCloseDocumentPreview(docTab.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className="ml-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                      aria-label="Close document preview"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </span>
                  </button>
                ))}
                </div>
                {inlineDealsFlag && (
                  <button
                    onClick={handleClosePanel}
                    className="flex-shrink-0 ml-auto mr-2 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cvc-blue"
                    aria-label="Close panel"
                    title="Close panel"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Tab Content Area */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden">{renderContent()}</div>
            </div>
          </div>
          </div>
        )}
      </div>
    </div>
  );
}
